import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { View, Text, Button, DropdownField, InputField, NavLink } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { evaluatorActions, Evaluator, EvaluatorType, TargetFormat } from "../../redux/reducers/evaluatorSlice";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { JsonSchemaBuilder } from "../../components/evaluations/JsonSchemaBuilder";
import { SuccessCriteriaBuilder } from "../../components/evaluations/SuccessCriteriaBuilder";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useEvaluatorSupportedModels } from "../../hooks/useEvaluatorSupportedModels";

const parseJsonField = (value: string, fieldLabel: string) => {
  if (!value.trim()) {
    return undefined;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    throw new Error(`${fieldLabel} must be valid JSON`);
  }
};

const stringifyJson = (value?: Record<string, unknown> | null) => {
  if (!value) {
    return "";
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return "";
  }
};

const evaluatorTypeOptions = [
  { value: EvaluatorType.JUDGE, label: "Judge" },
  { value: EvaluatorType.EXTRACTION, label: "Extraction" },
  // Scripts coming soon
  // { value: EvaluatorType.SCRIPT, label: "Script" },
];

const targetFormatOptions = [
  { value: TargetFormat.TRANSCRIPT, label: "Transcript" },
  { value: TargetFormat.EVENTS, label: "Events" },
  // Trace coming soon
  // { value: TargetFormat.TRACE, label: "Trace" },
  // Recording coming soon
  // { value: TargetFormat.RECORDING, label: "Recording" },
];

const defaultFormState = {
  title: "",
  evalType: EvaluatorType.JUDGE,
  targetFormat: TargetFormat.EVENTS,
  apiProvider: "",
  model: "",
  systemPrompt: "",
  responseSchema: "",
  successCriteria: "",
  code: "",
};

type EvaluatorFormValues = typeof defaultFormState;

export const EvaluatorDetailsPage = () => {
  const { evaluatorId } = useParams<{ evaluatorId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.session.user);
  const { organization } = useEffectiveOrganization();
  const userOrganization = useAppSelector((state) => (user?.organizationId ? state.organization.entities[user.organizationId] : null));

  const evaluator = useAppSelector((state) => (evaluatorId ? state.evaluator.entities[evaluatorId] : undefined));
  const evaluatorsIsLoading = useAppSelector((state) => state.evaluator.isLoading);
  const evaluatorsIsSaving = useAppSelector((state) => state.evaluator.isLoading);
  const evaluatorsIsDeleting = useAppSelector((state) => state.evaluator.isLoadingDelete);
  const { loading: supportedModelsLoading, getApiProviders, getModelsForProvider } = useEvaluatorSupportedModels();

  const [formError, setFormError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<EvaluatorFormValues>(defaultFormState);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    if (evaluatorId) {
      dispatch(evaluatorActions.getById({ id: evaluatorId }));
    }
  }, [dispatch, evaluatorId]);

  useEffect(() => {
    if (evaluator) {
      setFormValues({
        title: evaluator.title || "",
        evalType: evaluator.evalType,
        targetFormat: evaluator.targetFormat || TargetFormat.EVENTS,
        apiProvider: evaluator.apiProvider || "",
        model: evaluator.model || "",
        systemPrompt: evaluator.systemPrompt || "",
        responseSchema: stringifyJson(evaluator.responseSchema),
        successCriteria: stringifyJson(evaluator.successCriteria),
        code: evaluator.code || "",
      });
    }
  }, [evaluator]);

  const apiProviderOptions = useMemo(() => {
    return getApiProviders().map((provider) => ({
      value: provider,
      label: provider,
    }));
  }, [getApiProviders]);

  const modelOptions = useMemo(() => {
    return getModelsForProvider("text", formValues.apiProvider).map((model) => ({
      value: model,
      label: model,
    }));
  }, [formValues.apiProvider, getModelsForProvider]);

  useEffect(() => {
    if (formValues.evalType === EvaluatorType.SCRIPT) {
      return;
    }

    if (!formValues.apiProvider && apiProviderOptions.length > 0) {
      const nextProvider = apiProviderOptions[0].value;
      const nextModels = getModelsForProvider("text", nextProvider);
      setFormValues((prev) => ({
        ...prev,
        apiProvider: nextProvider,
        model: nextModels[0] || "",
      }));
    }
  }, [apiProviderOptions, formValues.apiProvider, formValues.evalType, getModelsForProvider]);

  useEffect(() => {
    if (formValues.evalType === EvaluatorType.SCRIPT) {
      return;
    }

    if (!formValues.apiProvider || modelOptions.length === 0) {
      return;
    }

    const hasModel = modelOptions.some((option) => option.value === formValues.model);
    if (!hasModel) {
      setFormValues((prev) => ({
        ...prev,
        model: modelOptions[0]?.value || "",
      }));
    }
  }, [formValues.apiProvider, formValues.evalType, formValues.model, modelOptions]);

  const responseSchemaObject = useMemo(() => {
    if (!formValues.responseSchema.trim()) {
      return null;
    }

    try {
      return JSON.parse(formValues.responseSchema);
    } catch (error) {
      return null;
    }
  }, [formValues.responseSchema]);

  const successCriteriaObject = useMemo(() => {
    if (!formValues.successCriteria.trim()) {
      return null;
    }

    try {
      return JSON.parse(formValues.successCriteria);
    } catch (error) {
      return null;
    }
  }, [formValues.successCriteria]);

  if (!evaluatorId) {
    return null;
  }

  if (evaluatorsIsLoading && !evaluator) {
    return (
      <View className="flex flex-col items-center justify-center h-full">
        <Text className="text-sm text-gray-500 dark:text-gray-400">Loading evaluator...</Text>
      </View>
    );
  }

  const handleFormChange =
    <K extends keyof EvaluatorFormValues>(key: K) =>
    (value: EvaluatorFormValues[K]) => {
      setFormValues((prev) => ({ ...prev, [key]: value }));
    };

  const handleApiProviderChange = (value: string) => {
    const nextModels = getModelsForProvider("text", value);
    setFormValues((prev) => ({
      ...prev,
      apiProvider: value,
      model: nextModels.includes(prev.model) ? prev.model : nextModels[0] || "",
    }));
  };

  const handleSave = async () => {
    if (!evaluator) {
      return;
    }

    try {
      const title = formValues.title.trim();
      if (!title) {
        throw new Error("Title is required.");
      }

      const payload: Partial<Evaluator> & { id: string } = {
        id: evaluator.id,
        organizationId: evaluator.organizationId,
        title,
        evalType: formValues.evalType,
        targetFormat: formValues.targetFormat,
      };

      if (formValues.evalType === EvaluatorType.SCRIPT) {
        if (!formValues.code.trim()) {
          throw new Error("Script code is required.");
        }
        payload.code = formValues.code;
        payload.systemPrompt = undefined;
        payload.responseSchema = undefined;
        payload.successCriteria = undefined;
        payload.apiProvider = undefined;
        payload.model = undefined;
      } else {
        if (!formValues.apiProvider) {
          throw new Error("Provider is required.");
        }
        if (!formValues.model) {
          throw new Error("Model is required.");
        }

        payload.apiProvider = formValues.apiProvider;
        payload.model = formValues.model;

        const systemPrompt = formValues.systemPrompt.trim();
        if (!systemPrompt) {
          throw new Error("System prompt is required.");
        }
        payload.systemPrompt = systemPrompt;

        const responseSchema = parseJsonField(formValues.responseSchema, "Response schema");
        if (formValues.evalType === EvaluatorType.JUDGE && !responseSchema) {
          throw new Error("Response schema is required for judge evaluators.");
        }
        payload.responseSchema = responseSchema;

        if (formValues.evalType === EvaluatorType.JUDGE) {
          const successCriteria = parseJsonField(formValues.successCriteria, "Success criteria");
          if (!successCriteria) {
            throw new Error("Success criteria is required for judge evaluators.");
          }
          payload.successCriteria = successCriteria;
        } else {
          payload.successCriteria = undefined;
        }

        payload.code = undefined;
      }

      setFormError(null);
      await dispatch(evaluatorActions.updateById(payload)).unwrap();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Unable to save evaluator");
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!evaluator) {
      return;
    }

    try {
      await dispatch(
        evaluatorActions.deleteById({
          id: evaluator.id,
        }),
      ).unwrap();
      navigate("/evaluators");
    } catch (error) {
      setFormError("Failed to delete evaluator. Please try again.");
      setShowDeleteDialog(false);
    }
  };

  return (
    <View className="flex flex-col flex-1 w-full min-h-full gap-6 p-6 bg-white dark:bg-slate-900 overflow-y-auto">
      <NavLink
        href="/evaluators"
        className="flex items-center w-fit p-0"
      >
        <Icon
          name="arrow-back"
          size={18}
          className="mr-1"
        />
        Evaluators
      </NavLink>

      <View className="flex flex-col gap-2">
        <Text className="text-2xl font-semibold text-gray-900 dark:text-white">{evaluator?.title || "Evaluator"}</Text>
        <Text className="text-sm text-gray-600 dark:text-gray-400">Edit evaluator configuration and metadata.</Text>
      </View>

      {formError ? (
        <View className="rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-3">
          <Text className="text-sm text-red-700 dark:text-red-300">{formError}</Text>
        </View>
      ) : null}

      <View className="flex flex-col gap-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-6 shadow-sm w-full">
        <InputField
          label="Title"
          placeholder="Give this evaluator a descriptive name"
          value={formValues.title}
          onChange={handleFormChange("title")}
        />
        <DropdownField
          label="Evaluator Type"
          tooltip="Evaluator Type cannot be modified"
          options={evaluatorTypeOptions}
          value={formValues.evalType}
          readOnly
          onChange={(value) => handleFormChange("evalType")(value as EvaluatorType)}
        />
        <DropdownField
          label="Target Format"
          tooltip="Target Format cannot be modified"
          options={targetFormatOptions}
          value={formValues.targetFormat}
          onChange={(value) => handleFormChange("targetFormat")(value as TargetFormat)}
          placeholder="Select target format"
          readOnly
        />
        {formValues.evalType !== EvaluatorType.SCRIPT ? (
          <View className="flex flex-col gap-4">
            <DropdownField
              label="Provider"
              options={apiProviderOptions}
              value={formValues.apiProvider}
              onChange={handleApiProviderChange}
              placeholder={supportedModelsLoading ? "Loading providers..." : "Select provider"}
              readOnly={supportedModelsLoading}
            />
            <DropdownField
              label="Model"
              options={modelOptions}
              value={formValues.model}
              onChange={(value) => handleFormChange("model")(value)}
              placeholder={supportedModelsLoading ? "Loading models..." : "Select model"}
              readOnly={supportedModelsLoading || !formValues.apiProvider}
            />
          </View>
        ) : null}
        {formValues.evalType !== EvaluatorType.SCRIPT ? (
          <InputField
            label="System Prompt"
            placeholder="Describe how the evaluator should behave"
            value={formValues.systemPrompt}
            onChange={handleFormChange("systemPrompt")}
            type="textarea"
            className="min-h-[128px]"
          />
        ) : null}
        {formValues.evalType === EvaluatorType.SCRIPT ? (
          <InputField
            label="Script"
            placeholder="Write a script to evaluate the session"
            value={formValues.code}
            onChange={handleFormChange("code")}
            type="textarea"
            className="min-h-[160px] font-mono"
          />
        ) : (
          <View className="flex flex-col gap-2">
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Response Schema</Text>
            <JsonSchemaBuilder
              value={responseSchemaObject as Record<string, unknown> | null}
              onChange={(schema) => handleFormChange("responseSchema")(schema ? JSON.stringify(schema, null, 2) : "")}
            />
          </View>
        )}
        {formValues.evalType === EvaluatorType.JUDGE ? (
          <View className="flex flex-col gap-2">
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Success Criteria</Text>
            <SuccessCriteriaBuilder
              schema={responseSchemaObject as Record<string, unknown> | null}
              value={successCriteriaObject as Record<string, unknown> | null}
              onChange={(criteria) => handleFormChange("successCriteria")(criteria ? JSON.stringify(criteria, null, 2) : "")}
            />
          </View>
        ) : null}
      </View>

      <View className="flex flex-row gap-3 justify-between flex-wrap">
        <Button
          type="button"
          variant="ghost"
          className="text-red-600 dark:text-red-400"
          onClick={handleDelete}
          isDisabled={evaluatorsIsDeleting || evaluatorsIsSaving}
        >
          Delete Evaluator
        </Button>
        <View className="flex flex-row gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() =>
              evaluator &&
              setFormValues({
                title: evaluator.title || "",
                evalType: evaluator.evalType,
                targetFormat: evaluator.targetFormat || TargetFormat.EVENTS,
                apiProvider: evaluator.apiProvider || "",
                model: evaluator.model || "",
                systemPrompt: evaluator.systemPrompt || "",
                responseSchema: stringifyJson(evaluator.responseSchema),
                successCriteria: stringifyJson(evaluator.successCriteria),
                code: evaluator.code || "",
              })
            }
            isDisabled={evaluatorsIsSaving}
          >
            Reset
          </Button>
          <Button
            type="button"
            variant="brand"
            onClick={handleSave}
            isDisabled={evaluatorsIsSaving}
            isLoading={evaluatorsIsSaving}
          >
            Save Changes
          </Button>
        </View>
      </View>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={confirmDelete}
        title="Delete Evaluator"
        message={`Are you sure you want to delete "${evaluator?.title}"?`}
        isLoading={evaluatorsIsDeleting}
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone. The evaluator will be permanently removed.</Text>
      </ConfirmDialog>
    </View>
  );
};
